import React, { useState } from "react";

import {
  Check,
  ChevronDown,
  ChevronUp,
  Copy,
  ExternalLink,
} from "lucide-react";

import { CreditUtilizationBar } from "~/components/debt/CreditUtilizationBar";
import { ManualFacturasTable } from "~/components/debt/ManualFacturasTable";
import { SalesNotesTable } from "~/components/debt/SalesNotesTable";
import { Button } from "~/components/ui/button";
import { Skeleton } from "~/components/ui/skeleton";
import { TableCell, TableRow } from "~/components/ui/table";
import {
  ClientDebtDetail,
  DebtType,
  useClientManualFacturasDebt,
  useClientSalesRequestsDebt,
} from "~/hooks/useNewDebtSummary";
import { formatCurrency } from "~/utils/helpers";

interface ClientDebtDropdownProps {
  client: ClientDebtDetail;
  activeTab: DebtType;
  cutoffDate?: Date;
  index?: number;
}

export const ClientDebtDropdown: React.FC<ClientDebtDropdownProps> = ({
  client,
  activeTab,
  cutoffDate,
  index = 0,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isCopied, setIsCopied] = useState(false);

  // Fetch detailed debt data when expanded
  const { data: salesRequestsDebt = [], isLoading: loadingSalesRequests } =
    useClientSalesRequestsDebt(client.client_id, cutoffDate);

  const { data: manualFacturasDebt = [], isLoading: loadingManualFacturas } =
    useClientManualFacturasDebt(client.client_id, cutoffDate);

  const handleToggle = () => {
    setIsExpanded(!isExpanded);
  };

  const handleCopyRut = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(client.client_rut);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    } catch (error) {
      console.error("Failed to copy RUT:", error);
    }
  };

  // Get debt amounts based on active tab
  const getTabSpecificDebt = () => {
    if (activeTab === "sales_requests") {
      // Calculate debt breakdown from actual sales requests data
      const totalTabDebt = client.sales_requests_debt;

      if (!salesRequestsDebt || salesRequestsDebt.length === 0) {
        return {
          totalDebt: totalTabDebt,
          prontoVencer: 0,
          sana: 0,
          vencida: 0,
        };
      }

      const breakdown = salesRequestsDebt.reduce(
        (acc, request) => {
          const debtAmount = request.debt_amount_at_cutoff;
          const daysTodue = request.days_to_due_at_cutoff;

          if (daysTodue < 0) {
            acc.vencida += debtAmount;
          } else if (daysTodue >= 0 && daysTodue < 5) {
            acc.prontoVencer += debtAmount;
          } else {
            acc.sana += debtAmount;
          }

          return acc;
        },
        { vencida: 0, prontoVencer: 0, sana: 0 }
      );

      return {
        totalDebt: totalTabDebt,
        ...breakdown,
      };
    } else {
      // Calculate debt breakdown from actual manual facturas data
      const totalTabDebt = client.manual_facturas_debt;

      if (!manualFacturasDebt || manualFacturasDebt.length === 0) {
        return {
          totalDebt: totalTabDebt,
          prontoVencer: 0,
          sana: 0,
          vencida: 0,
        };
      }

      const breakdown = manualFacturasDebt.reduce(
        (acc, factura) => {
          const debtAmount = factura.debt_amount_at_cutoff;
          const daysToDue = factura.days_to_due_at_cutoff;

          if (daysToDue < 0) {
            acc.vencida += debtAmount;
          } else if (daysToDue >= 0 && daysToDue < 5) {
            acc.prontoVencer += debtAmount;
          } else {
            acc.sana += debtAmount;
          }

          return acc;
        },
        { vencida: 0, prontoVencer: 0, sana: 0 }
      );

      return {
        totalDebt: totalTabDebt,
        ...breakdown,
      };
    }
  };

  const tabSpecificDebt = getTabSpecificDebt();

  return (
    <>
      {/* Main Table Row */}
      <TableRow
        style={{ animationDelay: `${Math.min(index * 40, 400)}ms` }}
        className="cursor-pointer transition-colors duration-300 animate-in fade-in-0 slide-in-from-top-1 fill-mode-both hover:bg-gray-50"
        onClick={handleToggle}
      >
        {/* Client Name */}
        <TableCell className="max-w-0 font-medium">
          <div className="flex min-w-0 items-center gap-2">
            <span
              className="min-w-0 flex-1 truncate text-gray-900"
              title={client.client_name}
            >
              {client.client_name}
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                window.open(`/clients/${client.client_id}`, "_blank");
              }}
              className="h-6 w-6 flex-shrink-0 p-0 hover:bg-blue-100"
              title="Ver Cliente"
            >
              <ExternalLink className="h-3 w-3 text-blue-600" />
            </Button>
          </div>
        </TableCell>

        {/* Tax ID (RUT) */}
        <TableCell className="max-w-0">
          <div className="flex min-w-0 items-center gap-1">
            <span
              className="min-w-0 flex-1 truncate font-mono text-sm"
              title={client.client_rut}
            >
              {client.client_rut}
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleCopyRut}
              className="h-6 w-6 flex-shrink-0 p-0 hover:bg-gray-200"
              title={isCopied ? "¡Copiado!" : "Copiar RUT"}
            >
              {isCopied ? (
                <Check className="h-3 w-3 text-green-600" />
              ) : (
                <Copy className="h-3 w-3 text-gray-500" />
              )}
            </Button>
          </div>
        </TableCell>

        {/* Total Debt */}
        <TableCell className="max-w-0">
          <span className="whitespace-nowrap font-semibold text-red-600">
            ${formatCurrency(tabSpecificDebt.totalDebt)}
          </span>
        </TableCell>

        {/* Expand Button */}
        <TableCell className="text-center">
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
            {isExpanded ? (
              <ChevronUp className="h-4 w-4" />
            ) : (
              <ChevronDown className="h-4 w-4" />
            )}
          </Button>
        </TableCell>
      </TableRow>

      {/* Expanded Details Row */}
      {isExpanded && (
        <TableRow>
          <TableCell colSpan={4} className="bg-gray-50 p-0">
            <div className="w-full overflow-hidden">
              <div className="w-full space-y-4 p-6">
                {/* Credit Utilization */}
                <div className="w-full max-w-2xl">
                  <CreditUtilizationBar
                    totalDebt={tabSpecificDebt.totalDebt}
                    creditLimit={client.credit_limit ?? 0}
                    utilizationPercent={
                      (client.credit_limit ?? 0) > 0
                        ? (tabSpecificDebt.totalDebt / client.credit_limit) *
                          100
                        : 0
                    }
                  />
                </div>

                {/* Client Info Details */}
                <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600">
                  <span className="whitespace-nowrap">
                    Vendedor: {client.assigned_seller}
                  </span>
                  {client.client_email && (
                    <span className="max-w-xs truncate">
                      Email: {client.client_email}
                    </span>
                  )}
                </div>

                {/* Debt Breakdown */}
                <div className="grid grid-cols-2 gap-3 text-sm lg:grid-cols-4">
                  <div className="rounded-lg bg-white p-3 text-center">
                    <div className="text-xs font-semibold text-red-600 lg:text-sm">
                      ${formatCurrency(tabSpecificDebt.totalDebt)}
                    </div>
                    <div className="text-xs text-gray-600">
                      {activeTab === "sales_requests"
                        ? "Deuda Notas"
                        : "Deuda Facturas"}
                    </div>
                  </div>
                  <div className="rounded-lg bg-white p-3 text-center">
                    <div className="text-xs font-semibold text-orange-600 lg:text-sm">
                      ${formatCurrency(tabSpecificDebt.prontoVencer)}
                    </div>
                    <div className="text-xs text-gray-600">Pronto a Vencer</div>
                  </div>
                  <div className="rounded-lg bg-white p-3 text-center">
                    <div className="text-xs font-semibold text-green-600 lg:text-sm">
                      ${formatCurrency(tabSpecificDebt.sana)}
                    </div>
                    <div className="text-xs text-gray-600">Deuda Sana</div>
                  </div>
                  <div className="rounded-lg bg-white p-3 text-center">
                    <div className="text-xs font-semibold text-red-600 lg:text-sm">
                      ${formatCurrency(tabSpecificDebt.vencida)}
                    </div>
                    <div className="text-xs text-gray-600">Deuda Vencida</div>
                  </div>
                </div>

                {/* Detailed Tables */}
                <div className="overflow-hidden rounded-lg bg-white p-4">
                  {activeTab === "sales_requests" && (
                    <>
                      {loadingSalesRequests ? (
                        <div className="space-y-2">
                          <Skeleton className="h-4 w-48" />
                          <Skeleton className="h-32 w-full" />
                        </div>
                      ) : (
                        <div className="overflow-x-auto">
                          <SalesNotesTable
                            salesRequests={salesRequestsDebt}
                            cutoffDate={cutoffDate}
                          />
                        </div>
                      )}
                    </>
                  )}

                  {activeTab === "manual_facturas" && (
                    <>
                      {loadingManualFacturas ? (
                        <div className="space-y-2">
                          <Skeleton className="h-4 w-48" />
                          <Skeleton className="h-32 w-full" />
                        </div>
                      ) : (
                        <div className="overflow-x-auto">
                          <ManualFacturasTable
                            manualFacturas={manualFacturasDebt}
                            cutoffDate={cutoffDate}
                          />
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </TableCell>
        </TableRow>
      )}
    </>
  );
};
